<script setup>
import { onMounted, ref, computed } from 'vue'
import Commons from '@/shared/utils/commons'
import TableList from '../others/TableList.vue'
import SlideDetailView from '../others/SlideDetailView.vue'
import RestaurantDetails from '../restaurant/RestaurantDetails.vue'
import RestaurantFormModal from '../restaurant/RestaurantFormModal.vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import { refreshBalances } from '@/app/boot.js'

const balancesLoading = ref(true)
const updateStatusTrigger = ref(null)
const showAddForm = ref(false)

onMounted(async () => {
  await refreshBalances()
  balancesLoading.value = false
})

const cols = computed(() => [
  {
    name: 'Restaurant',
    id: 'name',
    image: (el) => el.logo,
    value(el) {
      return [el.name, el.email]
    },
  },
  {
    name: 'Solde',
    id: 'balance',
    value(el) {
      if (el.balance === null || el.balance === undefined) return '—'
      return Commons.formatNumber(el.balance, { suffix: ' FCFA' })
    },
  },
  // {
  //   name: 'Stats (Plats/Livrés/Attente)',
  //   id: 'stats',
  //   value(el) {
  //     const pending = (el.totalOrders || 0) - (el.totalOrdersDelivered || 0)
  //     return `${el.totalDishes || 0} / ${el.totalOrdersDelivered || 0} / ${pending > 0 ? pending : 0}`
  //   }
  // },
  {
    name: 'Statut',
    id: 'status',
    filter: {
      key: 'status',
      default: '',
      options: [
        { name: 'Tout', value: '' },
        { name: 'Actif', value: 'active' },
        { name: 'Inactif', value: 'inactive' },
        { name: 'Suspendu', value: 'suspended' },
      ]
    },
    value(el) {
      const status = el.status || (el.active ? 'active' : 'inactive')
      const statusMap = {
        submitted: { label: 'Soumis', class: 'text-warning-600 bg-warning-50' },
        approved: { label: 'Apprové', class: 'text-success-600 bg-success-50' },
        draft: { label: 'Brouillon', class: 'text-warning-600 bg-warning-50' },
        under_review: { label: 'En Revue', class: 'text-warning-600 bg-warning-50' },
        rejected: { label: 'Rejeté', class: 'text-error-600 bg-error-50' },
      }
      const s = statusMap[status] || { label: status, class: 'text-gray-600 bg-gray-50' }
      return `<span class="px-2 py-1 rounded-full text-xs font-medium ${s.class}">${s.label}</span>`
    }
  },
  {
    name: ' ',
    id: 'action',
    icon: () => '/images/icons/dots.png',
    options(el) {
      const status = el.status;
      const opts = [
        { name: 'Voir détails', value: 'view', icon: 'pi pi-eye' }
      ];
      if (status === 'approved' || status === 'rejected') {
        opts.push({ 
          name: status === 'approved' ? 'Suspendre' : 'Activer', 
          value: 'toggle_status', 
          icon: status === 'approved' ? 'pi pi-ban' : 'pi pi-check' 
        });
      }
      return opts;
    },
    onClick(opt, el) {
      const action = typeof opt === 'object' ? opt.value : opt
      if (action === 'view') {
        detailData.value = el
      } else if (action === 'toggle_status') {
        const newStatus = el.status === 'approved' ? 'rejected' : 'approved';
        updateStatusTrigger.value.trigger({ id: el.id, status: newStatus });
      }
    },
    value() {},
  },
])

const detailData = ref(null)

const onStatusSuccess = () => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Succès',
    body: 'Statut du restaurant mis à jour',
  })
}

const onStatusError = (err) => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Erreur',
    body: err?.message || 'Impossible de mettre à jour le statut',
  })
}
</script>

<template>
  <div class="p-5 h-full overflow-auto bg-gray-50">
    <div v-if="balancesLoading" class="flex items-center justify-center h-64">
      <div class="text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
        <p class="text-gray-600">Chargement des soldes...</p>
      </div>
    </div>

    <div v-else>
      <div class="mb-6">
        <h1 class="text-3xl font-bold text-gray-800 mb-2">Gestion des Restaurants</h1>
        <p class="text-gray-600">Gérez les comptes, les statuts et consultez les performances des restaurants</p>
      </div>

      <TableList
        title="Liste des Restaurants"
        dataKey="restaurants"
        hasSearch
        :cols="cols"
        :buttons="[
          {
            title: '<i class=\'pi pi-plus mr-2\'></i> Ajouter un restaurant',
            onClick: () => (showAddForm = true),
          },
        ]"
        :refreshOnEvents="['RESTAURANT_UPDATED', 'RESTAURANT_CREATED']"
      ></TableList>
    </div>

    <RestaurantFormModal 
      v-if="showAddForm" 
      @close="showAddForm = false" 
      @success="showAddForm = false; Events.dispatch('RESTAURANT_CREATED')" 
    />

    <ActionTrigger
      ref="updateStatusTrigger"
      dataKey="updateRestaurant"
      @success="onStatusSuccess"
      @failed="onStatusError"
    />

    <SlideDetailView :isVisible="!!detailData" @hide="detailData = null">
      <RestaurantDetails v-if="detailData" :restaurant="detailData" />
    </SlideDetailView>
  </div>
</template>
